import { DashboardContent } from 'src/layouts/dashboard';
import { useNotify, useTranslate } from 'react-admin';
import { useCallback, useEffect, useState } from 'react';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import { Grid, Box, Button, Card, CardContent, TextField, Typography, IconButton, Paper, Divider } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import useFAQ from './useFAQ';
import { createFAQ, deleteFAQById, orderFAQ, resetFaq, updateFAQById } from 'src/pages/admin/services/whitelistService';

export interface FaqItem {
  id: string;
  slug?: string;
  position?: number;
  isDefault?: boolean;

  questionEn: string;
  questionVi: string;
  questionHi: string;
  questionZh: string;
  questionHk: string;

  answerEn: string;
  answerVi: string;
  answerHi: string;
  answerZh: string;
  answerHk: string;
}

const languages = [
  { key: 'En', label: 'English' },
  { key: 'Zh', label: '简体中文' },
  { key: 'Hk', label: '繁體中文' },
  { key: 'Vi', label: 'Tiếng Việt' },
  { key: 'Hi', label: 'हिन्दी' },
] as const;

const View = () => {
  const translate = useTranslate();
  const { faqs, retrieve } = useFAQ();
  const notify = useNotify();

  const [editedFaqs, setEditedFaqs] = useState<FaqItem[]>(faqs);

  const updateLocalFaq = useCallback(
    (id: string, field: keyof FaqItem, value: string | number | boolean) => {
      setEditedFaqs((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                [field]: value,
              }
            : item
        )
      );
    },
    []
  );

  useEffect(() => {
    retrieve();
  }, [retrieve]);

  useEffect(() => {
    setEditedFaqs(faqs ?? []);
  }, [faqs]);

  const handleSaveFaq = async (faq: FaqItem) => {
    try {
      if (faq.id === 'new') {
        const response = await createFAQ(
          faq.questionEn,
          faq.questionVi,
          faq.questionHi,
          faq.questionZh,
          faq.questionHk,
          faq.answerEn,
          faq.answerVi,
          faq.answerHi,
          faq.answerZh,
          faq.answerHk
        );
        if (response.status === 200) {
          notify(translate('whitelist.success'), { type: 'success', autoHideDuration: 1000 });
        } else {
          notify(translate('whitelist.failed'), { type: 'error', autoHideDuration: 1000 });
        }
      } else {
        const response = await updateFAQById(
          faq.id,
          faq.questionEn,
          faq.questionVi,
          faq.questionHi,
          faq.questionZh,
          faq.questionHk,
          faq.answerEn,
          faq.answerVi,
          faq.answerHi,
          faq.answerZh,
          faq.answerHk
        );
        if (response.status === 200) {
          notify(translate('whitelist.success'), { type: 'success', autoHideDuration: 1000 });
        } else {
          notify(translate('whitelist.failed'), { type: 'error', autoHideDuration: 1000 });
        }
      }
    } catch (error: any) {
      console.log('error: ', error);
      notify(error?.response?.data?.message || translate('whitelist.failed'), { type: 'error', autoHideDuration: 5000 });
    }

    retrieve();
  };

  const handleDeleteFaq = useCallback(async (id: string) => {
    const response = await deleteFAQById(id);

    if (response.data) {
      notify(translate('whitelist.success'), {
        type: 'success',
        autoHideDuration: 1000,
      });
    } else {
      notify(translate('whitelist.failed'), {
        type: 'error',
        autoHideDuration: 1000,
      });
    }
  },
  [notify, translate]
);

  const handleReorderFaq = useCallback(async (faqs: FaqItem[]) => {
    const orderedIds = faqs.map((faq) => faq.id);
    const response = await orderFAQ(orderedIds);
    if (response.status === 200) {
      notify(translate('whitelist.success'), { type: 'success', autoHideDuration: 1000 });
    } else {
      notify(translate('whitelist.failed'), { type: 'error', autoHideDuration: 1000 });
    }
  }, [notify, translate])

  const moveFaq = useCallback(
    (index: number, direction: 'up' | 'down') => {
      if (
        (direction === 'up' && index === 0) ||
        (direction === 'down' && index === editedFaqs.length - 1)
      ) {
        return;
      }

      const newFaqs = [...editedFaqs];

      const targetIndex = direction === 'up' ? index - 1 : index + 1;

      [newFaqs[index], newFaqs[targetIndex]] = [newFaqs[targetIndex], newFaqs[index]];

      setEditedFaqs(newFaqs);

      handleReorderFaq(newFaqs);
    },
    [editedFaqs, handleReorderFaq]
  );

  const deleteFaq = useCallback((id: string) => {
    const newFaqs = editedFaqs.filter((item) => item.id !== id);

    setEditedFaqs(newFaqs);

    handleDeleteFaq(id);

    handleReorderFaq(newFaqs);
  }, [editedFaqs, handleDeleteFaq, handleReorderFaq]);

  const addFaq = useCallback(() => {
    const newFaq: FaqItem = {
      id: 'new',

      questionEn: '',
      questionVi: '',
      questionHi: '',
      questionZh: '',
      questionHk: '',

      answerEn: '',
      answerVi: '',
      answerHi: '',
      answerZh: '',
      answerHk: '',
    };

    setEditedFaqs((prev) => [...prev, newFaq]);
  }, []);

  const handleResetToDefault = async () => {
    try {
      const response = await resetFaq();
      if (response.status === 200) {
        notify(translate('whitelist.success'), { type: 'success', autoHideDuration: 1000 });
        retrieve();
      } else {
        notify(translate('whitelist.failed'), { type: 'error', autoHideDuration: 1000 });
      }
    } catch (error: any) {
      console.log('error: ', error);
      notify(error?.response?.data?.message || translate('whitelist.failed'), { type: 'error', autoHideDuration: 5000 });
    }
  };

  return (
    <DashboardContent>
      <PermissionGuard resource="whitelist-faq" action="update" fallback={<ErrorPage403 />}>
        <Box
          sx={{
            width: '100%',
            px: { xs: 2, md: 4 },
            py: 4,
          }}
        >
          <Card
            sx={{
              width: '100%',
              borderRadius: 3,
              boxShadow: 3,
            }}
          >
            <CardContent
              sx={{
                p: { xs: 2, md: 4 },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="h5" component="h2" fontWeight="medium">
                  {translate('whitelist.faq.title')}
                </Typography>

                <Button variant="contained" startIcon={<AddIcon />} onClick={addFaq}>
                  {translate('whitelist.faq.addFaq')}
                </Button>
              </Box>

              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 3 }}>
                {translate('whitelist.faq.totleItems', {
                  count: editedFaqs.length,
                })}
              </Typography>

              <Divider sx={{ mb: 4 }} />

              {editedFaqs.map((faq, idx) => (
                <Paper
                  key={faq.id}
                  elevation={0}
                  sx={{
                    mb: 4,
                    p: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 3,
                    }}
                  >
                    <Typography variant="h6" fontWeight={600}>
                      FAQ #{idx + 1}
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SaveIcon />}
                        onClick={() => handleSaveFaq(faq)}
                      >
                        {translate('whitelist.save')}
                      </Button>

                      <IconButton
                        size="small"
                        onClick={() => moveFaq(idx, 'up')}
                        disabled={idx === 0}
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>

                      <IconButton
                        size="small"
                        onClick={() => moveFaq(idx, 'down')}
                        disabled={idx === editedFaqs.length - 1}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>

                      <IconButton size="small" color="error" onClick={() => deleteFaq(faq.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  <Grid container spacing={3}>
                    {languages.map((lang) => {
                      const questionKey = `question${lang.key}` as keyof FaqItem;

                      const answerKey = `answer${lang.key}` as keyof FaqItem;

                      return (
                        <Grid item xs={12} md={6} key={lang.key}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              height: '100%',
                              bgcolor: 'background.default',
                            }}
                          >
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                              {lang.label}
                            </Typography>

                            <TextField
                              label={`${translate('whitelist.faq.question')} (${lang.label})`}
                              fullWidth
                              size="small"
                              sx={{ mb: 2 }}
                              value={faq[questionKey] as string}
                              onChange={(e) => updateLocalFaq(faq.id, questionKey, e.target.value)}
                            />

                            <TextField
                              label={`${translate('whitelist.faq.answer')} (${lang.label})`}
                              fullWidth
                              multiline
                              minRows={5}
                              value={faq[answerKey] as string}
                              onChange={(e) => updateLocalFaq(faq.id, answerKey, e.target.value)}
                            />
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Paper>
              ))}
            </CardContent>
          </Card>

          <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box>
                <Typography variant="h6" component="h2" gutterBottom fontWeight="medium">
                  {translate('whitelist.faq.reset')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {translate('whitelist.faq.resetDescription')}
                </Typography>
              </Box>
              <Button variant="outlined" color="error" onClick={handleResetToDefault}>
                {translate('whitelist.faq.reset')}
              </Button>
            </Box>
          </Paper>
        </Box>
      </PermissionGuard>
    </DashboardContent>
  );
};

export default View;
